import { apiSlice } from "./apiSlice";
import {
  SchoolEntity,
  SchoolStatus,
  KinderGraduationStatus,
  KinderGraduationResult,
} from "../../services/schools/school.types";

export const schoolsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The backend exposes no search params for schools: the page fetches the
    // full list once (cached under a single entry) and filters client-side.
    getSchools: builder.query<SchoolEntity[], void>({
      query: () => ({
        url: "/schools",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "School" as const, id })),
              { type: "School" as const, id: "LIST" },
            ]
          : [{ type: "School" as const, id: "LIST" }],
    }),
    deleteSchool: builder.mutation<void, number>({
      query: (id) => ({
        url: `/schools/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "School", id: "LIST" }],
    }),
    // Access switch (SCRUM-748). Invalidating the list keeps the Escuelas
    // table's "Estado" column in sync with a toggle made from the edit view.
    updateSchoolStatus: builder.mutation<
      SchoolStatus,
      { id: number; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `/schools/${id}/status`,
        method: "PATCH",
        data: { is_active },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "School", id },
        { type: "School", id: "LIST" },
      ],
    }),
    // Kinder graduation (SCRUM-721). The status drives whether the action is
    // offered at all, so it is re-fetched after a run to lock the button.
    getKinderGraduationStatus: builder.query<KinderGraduationStatus, number>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/kinder-graduation`,
        method: "GET",
      }),
      providesTags: (_result, _error, schoolId) => [
        { type: "KinderGraduation", id: schoolId },
      ],
    }),
    graduateKinderStudents: builder.mutation<KinderGraduationResult, number>({
      query: (schoolId) => ({
        url: `/schools/${schoolId}/kinder-graduation`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, schoolId) => [
        { type: "KinderGraduation", id: schoolId },
        { type: "School", id: schoolId },
      ],
    }),
  }),
});

export const {
  useGetSchoolsQuery,
  useDeleteSchoolMutation,
  useUpdateSchoolStatusMutation,
  useGetKinderGraduationStatusQuery,
  useGraduateKinderStudentsMutation,
} = schoolsApi;
